import { find, keyBy, map, sortBy } from 'lodash'
import { useCallback } from 'react'
import { useEffectOnce } from 'react-use'

import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import useRemoteCollection, { Action } from 'civic-champs-shared/api/hooks/useRemoteCollection'
import { CheckOutInfo, CheckOutRole, EditableCheckOut } from '../types'
import useGetCheckOutInfo from './useGetCheckOutInfo'
import { EditCheckOutPrompt, EditCheckOutResult } from '../components/EditCheckOutPrompt'
import moment from 'moment'

export interface ExpectedCheckOutsData {
  checkOuts: EditableCheckOut[]
  loading: boolean
}

export interface ExpectedCheckOutsOperations {
  editCheckOut: (checkOut: EditableCheckOut) => Promise<void>
}

const mapFromCheckOutInfo = ({ shifts, expectedCheckOuts }: CheckOutInfo): EditableCheckOut[] => {
  const shiftsById = keyBy(shifts, shift => shift.encodedId)

  const editableCheckOuts = map(expectedCheckOuts, (expected): EditableCheckOut => {
    const { checkedInAt, expectedCheckOut: checkedOutAt, user } = expected

    let role: CheckOutRole | undefined
    const shift = shiftsById[expected.shiftId]
    if (shift) role = find(shift.roles, role => role.id === expected.expectedVolunteeringRoleId)

    return {
      activeVolunteerId: expected.id,
      user,
      shift,
      checkedInAt: moment(checkedInAt).toDate(),
      checkedOutAt: moment(checkedOutAt).toDate(),
      role,
    }
  })

  return sortBy(editableCheckOuts, ['shift.name', 'checkedInAt'])
}

const useEditCheckOut = (eagerReplace: Action<EditableCheckOut>) => {
  const showEditCheckOutPrompt = useShowPrompt(EditCheckOutPrompt)

  return useCallback(
    async (checkOut: EditableCheckOut) => {
      const result = (await showEditCheckOutPrompt({ checkOut })) as EditCheckOutResult
      if (result.proceed) {
        eagerReplace(result.checkOut)
      }
    },
    [eagerReplace, showEditCheckOutPrompt],
  )
}

const useExpectedCheckOutsCollection = (ids: number[]): [ExpectedCheckOutsData, ExpectedCheckOutsOperations] => {
  const [checkOuts, operations] = useRemoteCollection<EditableCheckOut>('activeVolunteerId')
  const [getCheckOutInfo, { loading, called }] = useGetCheckOutInfo()

  const { eagerReplace, syncCollection } = operations

  useEffectOnce(() => {
    ;(async () => {
      const checkOutInfo = await getCheckOutInfo(ids)
      const checkOuts = mapFromCheckOutInfo(checkOutInfo)
      syncCollection(checkOuts)
    })()
  })

  const editCheckOut = useEditCheckOut(eagerReplace)

  return [
    {
      checkOuts,
      loading: loading || !called,
    },
    {
      editCheckOut,
    },
  ]
}

export default useExpectedCheckOutsCollection
